"use client"

import { useEffect, useRef, type ReactNode } from "react"
import { useScrollIndex } from "../hooks"
import { cn } from "../utils"

interface CarouselDotsProps {
  count: number
  activeIndex?: number
}

export function CarouselDots({ count, activeIndex = 0 }: CarouselDotsProps) {
  return (
    <div className="flex justify-center gap-s py-s">
      {Array.from({ length: count }).map((_, i) => (
        <div
          key={i}
          className={`size-1.5 rounded-full transition-all duration-300 ${
            i === activeIndex ? "bg-primary w-6" : "bg-muted"
          }`}
        />
      ))}
    </div>
  )
}

interface CarouselProps<T> {
  items: T[]
  renderItem: (item: T) => ReactNode
  getKey: (item: T) => string | number
  className?: string
  onIndexChange?: (index: number) => void
}

export function Carousel<T>({ items, renderItem, getKey, className, onIndexChange }: CarouselProps<T>) {
  const scrollRef = useRef<HTMLDivElement>(null)
  const activeIndex = useScrollIndex(scrollRef)

  useEffect(() => {
    onIndexChange?.(activeIndex)
  }, [activeIndex, onIndexChange])

  if (items.length === 0) return null

  return (
    <div className="flex flex-col gap-xs">
      <div
        ref={scrollRef}
        className={cn("flex gap-s overflow-x-auto snap-x snap-mandatory px-m scrollbar-hide", className)}
      >
        {items.map((item) => (
          <div key={getKey(item)} className="snap-start flex-none">
            {renderItem(item)}
          </div>
        ))}
      </div>
      <CarouselDots count={items.length} activeIndex={activeIndex} />
    </div>
  )
}
